'use client'
import { useGetFareCalendarQuery } from '@/src/api/rtkQuery/calendarApi'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { getFormattedDate } from '@/src/utils/date'
import { Box, Button, Menu } from '@mui/material'
import dayjs from 'dayjs'
import { useParams, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Calendar from '../Calendar'
import { IRange } from '../hooks/search-inputs-manager'
import { useTranslations } from 'next-intl'

interface IDatePicker {
    isRoundTrip?: boolean
    range: IRange
    dateClick: (dates: any, range: IRange) => void
    index?: number
    multiCityDestinations?: Record<string, any>
    onAddReturn?: () => void
    showAddReturn?: boolean
    minDate?: Date | null
    active: boolean
    onActivate: () => void
    onClose: () => void
}

const DatePicker = (props: IDatePicker) => {
    const routeParams = useParams()
    const locale = (routeParams?.locale as string) || 'en'
    const t = useTranslations('common')
    const { dateClick, index = 1, multiCityDestinations = {}, range, isRoundTrip, active, onActivate, onClose, onAddReturn, showAddReturn = false, minDate } = props

    const [activeOnward, setActiveOnward] = useState(false)
    const [activeReturn, setActiveReturn] = useState(false)
    const [shouldClearFareData, setShouldClearFareData] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const returnButtonRef = useRef<HTMLButtonElement>(null)
    
    // Get search params and jsons for API call
    const params = useSearchParams()
    const { jsons, isPerPaxPricingEnabled } = useSearchResults()
    const { formatCurrency } = useFormatCurrencyForSrp()
    
    // Optimized: Memoize search parameters to prevent recalculation on every render
    // Only recalculates when params object changes, not on every render
    const searchParams = useMemo(() => ({
        origin: params?.get('from') || '',
        dest: params?.get('to') || '',
        adult: Number(params?.get('adults')) || 1,
        child: Number(params?.get('childs')) || 0,
        infant: Number(params?.get('infants')) || 0,
        cabin: params?.get('class')?.toUpperCase() || 'ECONOMY',
        returnDate: params?.get('return_date') || ''
    }), [params])
    
    // Optimized: Memoize derived values to prevent recalculation
    const partnerId = useMemo(() => jsons?.userInfo?.partnerId || '', [jsons?.userInfo?.partnerId])
    const perPaxPricingRequired = useMemo(() => !searchParams.returnDate && isPerPaxPricingEnabled, [searchParams.returnDate, isPerPaxPricingEnabled])

    // Optimized: useCallback to prevent function recreation on every render
    const handleOnwardClick = useCallback(() => {
        if (active && activeOnward) {
            // If already active and onward is selected, close
            onClose()
        } else {
            // Activate component and set onward as active
            setActiveOnward(true)
            setActiveReturn(false)
            onActivate()
        }
    }, [active, activeOnward, onClose, onActivate])

    const handleReturnClick = useCallback(() => {
        if (active && activeReturn) {
            // If already active and return is selected, close
            onClose()
        } else {
            // Activate component and set return as active
            setActiveOnward(false)
            setActiveReturn(true)
            setShouldClearFareData(true) // Clear fare data when switching to return
            onActivate()
        }
    }, [active, activeReturn, onClose, onActivate])

    const handleMenuClose = useCallback(() => {
        setActiveOnward(false)
        setActiveReturn(false)
        onClose()
    }, [onClose])

    // Calculate date range and payload from monthRange string
    // useCallback: Prevents function recreation unless search params change
    const calculateFareCalendarPayload = useCallback((monthRange: string) => {
        if (!monthRange || !searchParams.origin || !searchParams.dest) return null
        
        // Parse monthRange: "DD-MM-YYYY_DD-MM-YYYY"
        const [startMonthStr, endMonthStr] = monthRange.split('_')
        if (!startMonthStr || !endMonthStr) return null
        
        const startMonth = dayjs(startMonthStr, 'DD-MM-YYYY')
        const endMonth = dayjs(endMonthStr, 'DD-MM-YYYY')
        const today = dayjs().startOf('day')
        
        // Calculate startDate: today or first of month, whichever is later
        const startDate = startMonth.isBefore(today) ? today : startMonth.startOf('month')
        
        // Calculate daysCount: difference between startDate and end of endMonth
        const daysCount = endMonth.endOf('month').diff(startDate, 'day') + 1
        
        if (daysCount <= 0) return null
        
        return {
            queryContext: {
                startDate: startDate.format('DD/MM/YYYY'),
                daysCount,
                cabin: searchParams.cabin,
                origin: searchParams.origin,
                destination: searchParams.dest,
                paxTypeContext: {
                    adultCount: searchParams.adult,
                    childCount: searchParams.child,
                    infantCount: searchParams.infant
                }
            },
            demandContext: {
                partnerId
            }
        }
    }, [searchParams, partnerId])
    
    // State for current API payload
    const [currentPayload, setCurrentPayload] = useState<ReturnType<typeof calculateFareCalendarPayload>>(null)
    
    // Make API call when payload is available
    // skip: Only make API call for one-way trips (not round trip) when we have payload and required search params
    const { 
        data: fareCalendarData, 
        error: fareCalendarError, 
        isError: isFareCalendarError 
    } = useGetFareCalendarQuery(
        {
            payload: currentPayload!,
            perPaxPricingRequired
        },
        {
            skip: isRoundTrip || !currentPayload || !searchParams.origin || !searchParams.dest
        }
    )
    
    // Use derived values from RTK Query data instead of syncing to state
    // This prevents unnecessary re-renders and simplifies the code
    const fareCalendarFromApi = useMemo(() => {
        if (isFareCalendarError) {
            console.warn('Fare calendar API error:', fareCalendarError)
            return { fareCalendar: {}, cheapestPricesByMonth: {} }
        }
        return {
            fareCalendar: fareCalendarData?.fareCalendar || {},
            cheapestPricesByMonth: fareCalendarData?.cheapestPricesByMonth || {}
        }
    }, [fareCalendarData, isFareCalendarError, fareCalendarError])

    // Final fare calendar data: use API data unless we need to clear it
    const fareCalendar = useMemo(() => {
        return shouldClearFareData ? {} : fareCalendarFromApi.fareCalendar
    }, [shouldClearFareData, fareCalendarFromApi.fareCalendar])

    const cheapestPricesByMonth = useMemo(() => {
        return shouldClearFareData ? {} : fareCalendarFromApi.cheapestPricesByMonth
    }, [shouldClearFareData, fareCalendarFromApi.cheapestPricesByMonth])

    // Reset states when component becomes inactive
    useEffect(() => {
        if (!active) {
            setActiveOnward(false)
            setActiveReturn(false)
            setShouldClearFareData(false)
            setCurrentPayload(null)
        } else if (!activeOnward && !activeReturn) {
            // Default to onward when becoming active externally
            setActiveOnward(true)
        }
    }, [active]) // Only depend on active to prevent re-render loops

    // Initial API call when calendar opens (only for one-way trips)
    // calculateFareCalendarPayload is stable (useCallback) so including it won't cause extra re-renders
    useEffect(() => {
        // Only set initial payload for one-way trips when calendar is active and we don't have one yet
        if (!isRoundTrip && active && (activeOnward || activeReturn) && searchParams.origin && searchParams.dest && !currentPayload) {
            // Calculate initial month range based on selected date (if available) or today
            // This ensures API call matches the month being displayed in calendar
            const baseDate = range?.from ? dayjs(range.from) : dayjs()
            const startMonth = baseDate.startOf('month').format('DD-MM-YYYY')
            const endMonth = baseDate.add(1, 'month').endOf('month').format('DD-MM-YYYY')
            const initialMonthRange = `${startMonth}_${endMonth}`
            
            const payload = calculateFareCalendarPayload(initialMonthRange)
            if (payload) {
                setCurrentPayload(payload)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRoundTrip, active, activeOnward, activeReturn, searchParams.origin, searchParams.dest, calculateFareCalendarPayload, range?.from])
    
    // Optimized: useCallback to prevent function recreation
    const directionClick = useCallback((departRange: string) => {
        // Only trigger API call for one-way trips
        if (!isRoundTrip) {
            // Calculate payload from monthRange and trigger API call
            const payload = calculateFareCalendarPayload(departRange)
            if (payload) {
                setCurrentPayload(payload)
            }
        }
    }, [calculateFareCalendarPayload, isRoundTrip])

    const { formatDate } = useFormatDate()
    // Simple formatting function - no need for useCallback as formatDate is stable
    const formatDateToDisplay = (date: any) => {
        if (date) {
            return formatDate(getFormattedDate(date, 'MM/DD/YYYY'), 'ddd, DD MMM')
        }
        return 'Select Date'
    }

    const setDates = useCallback((dates: any) => {
        dateClick(dates, range)

        if (isRoundTrip && dates['to'] && activeReturn) {
            // Both dates selected for round trip - close calendar
            handleMenuClose()
        } else if (isRoundTrip && dates['from'] && activeOnward) {
            // Only departure date selected for round trip - switch to return
            setActiveOnward(false)
            setActiveReturn(true)
            // Keep calendar open for return date selection
        } else {
            // One way trip completed - close calendar
            handleMenuClose()
        }
    }, [dateClick, range, isRoundTrip, activeReturn, activeOnward, handleMenuClose])

    // Simple function to get anchor element - no need for useCallback
    const getAnchorEl = () => {
        if (activeReturn && returnButtonRef.current) {
            return returnButtonRef.current
        }
        return buttonRef.current
    }

    // Optimized: Extract inline onClick handler
    const handleAddReturn = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        if (onAddReturn) {
            onAddReturn()
            // After switching to round trip, open calendar for return date
            setTimeout(() => {
                setActiveReturn(true)
                setActiveOnward(false)
                onActivate()
            }, 50)
        }
    }, [onAddReturn, onActivate])

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'max-content', px: 2 }}>
                <Button
                    onClick={handleOnwardClick}
                    ref={buttonRef}
                    sx={{
                        textTransform: 'none',
                        p: 0,
                        '&:hover': { backgroundColor: 'transparent' },
                        fontWeight: activeOnward ? 600 : 400,
                        color: activeOnward ? 'primary.main' : 'inherit'
                    }}
                >
                    {formatDateToDisplay(range['from'])}
                </Button>
                {isRoundTrip ? (
                    <>
                        -{' '}
                        <Button
                            ref={returnButtonRef}
                            sx={{
                                textTransform: 'none',
                                p: 0,
                                '&:hover': { backgroundColor: 'transparent' },
                                fontWeight: activeReturn ? 600 : 400,
                                color: activeReturn ? 'primary.main' : 'inherit'
                            }}
                            onClick={handleReturnClick}
                        >
                            {formatDateToDisplay(range['to'])}
                        </Button>
                    </>
                ) : showAddReturn ? (
                    <>
                        -{' '}
                        <Button
                            sx={{
                                textTransform: 'none',
                                p: 0,
                                '&:hover': { backgroundColor: 'transparent' },
                                color: 'primary.main',
                                fontWeight: 400
                            }}
                            onClick={handleAddReturn}
                        >
                            {t('addReturn')}
                        </Button>
                    </>
                ) : null}
            </Box>

            <Menu
                anchorEl={getAnchorEl()}
                onClose={handleMenuClose}
                open={(active || activeOnward || activeReturn) && !!getAnchorEl()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                disablePortal={false}
                sx={{ top: 15 }}
            >
                <Box
                    sx={{
                        left: '50%',
                        height: 365,
                        top: 25,
                        borderLeft: '1px dotted rgb(187, 187, 187)',
                        position: 'absolute'
                    }}
                ></Box>
                <Calendar
                    isRtl={locale === 'ar'}
                    className="w-100p"
                    title="New Delhi → Bangalore"
                    selectedDates={range}
                    roundTrip={isRoundTrip}
                    onClickCalendar={setDates}
                    fareCalendar={fareCalendar}
                    cheapestPricesByMonth={cheapestPricesByMonth}
                    directionClick={directionClick}
                    activeOnward={activeOnward}
                    activeReturn={activeReturn}
                    isMulticity={false}
                    multiCityDestinations={multiCityDestinations}
                    index={index}
                    minDate={minDate}
                    formatCurrency={formatCurrency}
                />
            </Menu>
        </Box>
    )
}

export default DatePicker
